import { Button } from "@aurumiq/ui/components/button";
import { CopyButton } from "@aurumiq/ui/composite/copy-button";
import { type ColumnDef, createColumnHelper, DataTable } from "@aurumiq/ui/composite/data-table";
import { StatusTag } from "@aurumiq/ui/composite/status-tag";
import { Apple, Check, Download, Monitor, Terminal } from "lucide-react";
import type { ArtifactPlatform, ReleaseArtifact } from "@/lib/types";

export const PLATFORM_LABEL: Record<ArtifactPlatform, string> = {
  win: "Windows",
  mac: "macOS",
  linux: "Linux",
};

const ICONS = { win: Monitor, mac: Apple, linux: Terminal } as const;

export function PlatformIcon({
  platform,
  className,
}: {
  platform: ArtifactPlatform;
  className?: string;
}) {
  const Icon = ICONS[platform] ?? Monitor;
  return (
    <Icon
      className={className ?? "size-4"}
      aria-label={PLATFORM_LABEL[platform] ?? platform}
      role="img"
    />
  );
}

export function formatBytes(n: number): string {
  if (!n) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let v = n;
  let u = 0;
  while (v >= 1024 && u < units.length - 1) {
    v /= 1024;
    u++;
  }
  return `${v.toFixed(u === 0 ? 0 : 1)} ${units[u]}`;
}

/** Icon strip summarising which platforms a release ships for, plus the artifact count. */
export function ArtifactSummary({ artifacts }: { artifacts: ReleaseArtifact[] }) {
  const platforms = [...new Set(artifacts.map((a) => a.platform))];
  const total = artifacts.reduce((s, a) => s + a.size, 0);
  return (
    <span className="inline-flex items-center gap-2">
      <span className="tabular">{artifacts.length}</span>
      <span className="inline-flex gap-1 text-muted-foreground">
        {platforms.map((p) => (
          <PlatformIcon key={p} platform={p} />
        ))}
      </span>
      {total ? <span className="text-muted-foreground text-xs">{formatBytes(total)}</span> : null}
    </span>
  );
}

const col = createColumnHelper<ReleaseArtifact>();

const columns = [
  col.accessor("filename", {
    header: "File",
    cell: (c) => <span className="font-mono text-xs">{c.getValue()}</span>,
  }),
  col.accessor("platform", {
    header: "Platform",
    cell: (c) => (
      <span className="inline-flex items-center gap-1.5">
        <PlatformIcon platform={c.getValue()} />
        {PLATFORM_LABEL[c.getValue()] ?? c.getValue()}
        <span className="text-muted-foreground text-xs">
          {c.row.original.arch} · {c.row.original.kind}
        </span>
      </span>
    ),
  }),
  col.accessor("size", {
    header: "Size",
    meta: { numeric: true },
    cell: (c) => formatBytes(c.getValue()),
  }),
  col.accessor("sha512", {
    header: "SHA-512",
    cell: (c) => (
      <span className="inline-flex items-center gap-1">
        <span className="font-mono text-xs" title={c.getValue()}>
          {c.getValue().slice(0, 12)}…
        </span>
        <CopyButton value={c.getValue()} variant="ghost" />
      </span>
    ),
  }),
  col.accessor("uploaded", {
    header: "Uploaded",
    cell: (c) =>
      c.getValue() ? (
        <StatusTag variant="ok">
          <Check className="inline size-3" aria-hidden="true" /> YES
        </StatusTag>
      ) : (
        <StatusTag variant="warn">PENDING</StatusTag>
      ),
  }),
  col.display({
    id: "download",
    header: "",
    meta: { numeric: true },
    cell: (c) =>
      c.row.original.url ? (
        <Button asChild variant="ghost" size="sm">
          <a href={c.row.original.url} target="_blank" rel="noreferrer" download>
            <Download /> Download
          </a>
        </Button>
      ) : null,
  }),
] as ColumnDef<ReleaseArtifact, unknown>[];

export function ArtifactTable({ artifacts }: { artifacts: ReleaseArtifact[] }) {
  return (
    <DataTable
      columns={columns}
      data={artifacts}
      getRowId={(a) => a.id}
      emptyMessage="No artifacts uploaded yet. Use the CI upload script."
    />
  );
}
